/**
 * « Publier dans l'app » (lot 4) — rubrique admin et écran « Tester ».
 *
 * Le serveur revalide le brouillon (`animation-publish`), écrit une version
 * immuable et la met en ligne sur la SKILLCARD (page de la carte, fenêtre
 * « Cours ») et sur son lien public `/animation/<slug>`. Sans accès à la
 * carte, l'élève reçoit la partie gratuite seulement. Republier = nouvelle
 * version, même lien ; « Retirer de l'app » la rend invisible.
 *
 * « J'ai relu » (0672) : la revue reste humaine ; on publie le brouillon
 * relu EN L'ÉTAT (toute modification ensuite demande de le relire).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, CheckCircle2, Copy, ExternalLink, EyeOff, Send } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  hasUnpublishedChanges,
  isGenerating,
  isReviewed,
  useMarkReviewed,
  usePublishAnimation,
  useUnpublishAnimation,
  type AnimationRow,
} from '@/lib/animations/api';
import { animationShareUrl } from '@/lib/animations/published';

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export type PublishableRow = Pick<
  AnimationRow,
  | 'id'
  | 'status'
  | 'slug'
  | 'published_version'
  | 'published_at'
  | 'updated_at'
  | 'generation_status'
  | 'reviewed_at'
>;

export function AnimationAppPublish({
  row,
  health,
}: {
  row: PublishableRow;
  /** brouillon revu sur place : valide ? défauts que l'élève verrait ? */
  health: { valid: boolean; quality: number } | null;
}) {
  const publish = usePublishAnimation();
  const unpublish = useUnpublishAnimation();
  const review = useMarkReviewed();
  const reviewed = isReviewed(row);
  const [copied, setCopied] = useState(false);
  const live = row.status === 'published';
  const changed = hasUnpublishedChanges(row);
  const running = isGenerating(row);
  const blocked = running
    ? 'Une génération est en cours.'
    : !health?.valid
      ? 'Il faut un brouillon valide : corrige les erreurs ou régénère.'
      : null;

  const copy = async () => {
    if (!row.slug) return;
    try {
      await navigator.clipboard.writeText(animationShareUrl(row.slug));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* presse-papiers refusé : le lien reste visible */
    }
  };

  return (
    <div className="space-y-2" data-testid="animation-app-publish">
      {live ? (
        <div
          className={cn(
            'rounded-xl px-3 py-2 text-[13px]',
            changed ? 'bg-warning-50 text-ink-700' : 'bg-success-50 text-success-600',
          )}
        >
          <p className="font-semibold">
            En ligne dans l’app · version {row.published_version}
            {row.published_at && <> · depuis le {dateFmt.format(new Date(row.published_at))}</>}
          </p>
          {changed && <p>Le brouillon a changé depuis : republie pour mettre l’app à jour.</p>}
        </div>
      ) : (
        <p className="text-ink-600 text-[13px]">
          {row.published_version
            ? `Retirée de l’app (dernière version publiée : ${row.published_version}).`
            : 'Pas encore dans l’app.'}{' '}
          Publiée, elle apparaît sur la SKILLCARD et sur son lien public ; la partie « À toi » n’est
          servie qu’aux élèves qui ont la carte.
        </p>
      )}
      {blocked && <p className="text-error-600 text-[13px]">{blocked}</p>}
      {!blocked && health && health.quality > 0 && (
        <p className="text-ink-700 text-[13px]">
          {health.quality} défaut{health.quality > 1 ? 's' : ''} de mise en page, de rythme ou de
          figure reste
          {health.quality > 1 ? 'nt' : ''} : un élève le{health.quality > 1 ? 's' : ''} verra.
        </p>
      )}

      {!blocked &&
        (reviewed ? (
          <p className="text-success-600 flex items-center gap-1.5 text-[13px] font-semibold">
            <CheckCircle2 className="h-4 w-4" aria-hidden /> Relu
            {row.reviewed_at && <> le {dateFmt.format(new Date(row.reviewed_at))}</>}
          </p>
        ) : (
          <label className="text-ink-800 flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-primary-500 h-5 w-5"
              checked={false}
              disabled={review.isPending}
              onChange={() => review.mutate(row.id)}
            />
            J’ai relu l’animation (calculs, figure, voix)
          </label>
        ))}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!!blocked || !reviewed || publish.isPending}
          onClick={() => publish.mutate(row.id)}
          className="bg-primary-600 hover:bg-primary-700 flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          <Send className="h-4 w-4" aria-hidden />
          {publish.isPending
            ? 'Publication…'
            : live
              ? 'Republier'
              : row.published_version
                ? 'Remettre dans l’app'
                : 'Publier dans l’app'}
        </button>
        {live && row.slug && (
          <>
            <Link
              to="/animation/$slug"
              params={{ slug: row.slug }}
              className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold"
            >
              <ExternalLink className="h-4 w-4" aria-hidden /> Voir la page
            </Link>
            <button
              type="button"
              onClick={() => void copy()}
              className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold"
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {copied ? 'Lien copié' : 'Copier le lien'}
            </button>
            <button
              type="button"
              disabled={unpublish.isPending}
              onClick={() => unpublish.mutate(row.id)}
              className="text-ink-600 hover:bg-ink-100 flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold disabled:opacity-50"
            >
              <EyeOff className="h-4 w-4" aria-hidden /> Retirer de l’app
            </button>
          </>
        )}
      </div>

      {publish.data && !publish.isPending && (
        <p role="status" className="text-success-600 text-[13px] font-semibold">
          Version {publish.data.version_no} publiée
          {publish.data.locked > 0 &&
            ` · ${publish.data.locked} scène${publish.data.locked > 1 ? 's' : ''} « À toi » réservée${publish.data.locked > 1 ? 's' : ''} aux élèves qui ont la carte`}
          .
        </p>
      )}
      {(publish.error || unpublish.error || review.error) && (
        <p role="alert" className="text-error-600 text-[13px]">
          {(publish.error ?? unpublish.error ?? review.error)?.message}
        </p>
      )}
    </div>
  );
}
